"use client";

import { ErrorOutline, Home, Refresh, Send } from "@mui/icons-material";
import { Box, Button, Typography, TextField, Container, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { logError, trackEvent } from "@/utils/appInsights";
export default function GlobalError({
  error,
  reset
}: {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  useEffect(() => {
    // Track error in Application Insights
    logError(error, {
      errorBoundary: "global",
      digest: error.digest,
      message: error.message,
      stack: error.stack
    });
  }, [error]);
  const handleSendFeedback = () => {
    if (feedback.trim()) {
      // Send user feedback to Application Insights
      trackEvent("User_Error_Feedback", {
        errorDigest: error.digest || "unknown",
        errorMessage: error.message,
        userFeedback: feedback,
        timestamp: new Date().toISOString()
      });
      setFeedbackSent(true);
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackSent(false);
        setFeedback("");
      }, 2000);
    }
  };
  const isChunkLoadError = error.name === "ChunkLoadError" || error.message?.includes("Loading chunk");
  const handleTryAgain = () => {
    if (isChunkLoadError) {
      window.location.reload();
    } else {
      reset();
    }
  };
  const handleGoHome = () => {
    window.location.href = "/dashboard";
  };
  return <html data-sentry-component="GlobalError" data-sentry-source-file="global-error.tsx">
      <body style={{
      margin: 0,
      padding: 0,
      backgroundColor: "#F9FAFB"
    }}>
        <Container maxWidth="md" sx={{
        py: 8
      }} data-sentry-element="Container" data-sentry-source-file="global-error.tsx">
          <Paper elevation={0} sx={{
          p: 6,
          textAlign: "center",
          borderRadius: 2,
          border: "1px solid #E5E7EB"
        }} data-sentry-element="Paper" data-sentry-source-file="global-error.tsx">
            {/* Error Icon */}
            <Box sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#FEF2F2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px"
          }} data-sentry-element="Box" data-sentry-source-file="global-error.tsx">
              <ErrorOutline sx={{
              fontSize: 48,
              color: "#DC2626"
            }} data-sentry-element="ErrorOutline" data-sentry-source-file="global-error.tsx" />
            </Box>

            {/* Error Title */}
            <Typography variant="h4" sx={{
            fontWeight: 600,
            color: "#1F2937",
            mb: 2
          }} data-sentry-element="Typography" data-sentry-source-file="global-error.tsx">
              Oops! Something went wrong
            </Typography>

            {/* Error Description */}
            <Typography variant="body1" sx={{
            color: "#6B7280",
            mb: 1,
            maxWidth: 500,
            margin: "0 auto 8px"
          }} data-sentry-element="Typography" data-sentry-source-file="global-error.tsx">
              We're sorry, but something unexpected happened. Our team has been notified and we're working to fix it.
            </Typography>

            {/* Error ID */}
            {error.digest && <Typography variant="caption" sx={{
            color: "#9CA3AF",
            display: "block",
            mb: 4,
            fontFamily: "monospace"
          }}>
                Error ID: {error.digest}
              </Typography>}

            {/* Action Buttons */}
            <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 3
          }} data-sentry-element="Box" data-sentry-source-file="global-error.tsx">
              <Button variant="contained" startIcon={<Refresh />} onClick={handleTryAgain} sx={{
              backgroundColor: "#475569",
              "&:hover": {
                backgroundColor: "#334155"
              },
              textTransform: "none",
              px: 3
            }} data-sentry-element="Button" data-sentry-source-file="global-error.tsx">
                Try Again
              </Button>

              <Button variant="outlined" startIcon={<Home />} onClick={handleGoHome} sx={{
              borderColor: "#D1D5DB",
              color: "#6B7280",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB"
              },
              textTransform: "none",
              px: 3
            }} data-sentry-element="Button" data-sentry-source-file="global-error.tsx">
                Go Home
              </Button>
            </Box>

            {/* Feedback Section */}
            {!showFeedback ? <Button variant="text" size="small" onClick={() => setShowFeedback(true)} sx={{
            color: "#6B7280",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline"
            }
          }}>
                Report this error
              </Button> : <Box sx={{
            mt: 3,
            pt: 3,
            borderTop: "1px solid #E5E7EB"
          }}>
                {!feedbackSent ? <>
                    <Typography variant="body2" sx={{
                color: "#374151",
                mb: 2,
                textAlign: "left"
              }}>
                      Help us improve by describing what you were doing when this error occurred:
                    </Typography>
                    <TextField fullWidth multiline rows={3} placeholder="What were you trying to do?" value={feedback} onChange={e => setFeedback(e.target.value)} sx={{
                mb: 2
              }} />
                    <Box sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end"
              }}>
                      <Button variant="text" onClick={() => setShowFeedback(false)} sx={{
                  textTransform: "none",
                  color: "#6B7280"
                }}>
                        Cancel
                      </Button>
                      <Button variant="contained" startIcon={<Send />} onClick={handleSendFeedback} disabled={!feedback.trim()} sx={{
                  backgroundColor: "#475569",
                  "&:hover": {
                    backgroundColor: "#334155"
                  },
                  textTransform: "none"
                }}>
                        Send Feedback
                      </Button>
                    </Box>
                  </> : <Box sx={{
              p: 2,
              backgroundColor: "#DCFCE7",
              borderRadius: 1
            }}>
                    <Typography variant="body2" sx={{
                color: "#166534",
                fontWeight: 500
              }}>
                      ✓ Thank you for your feedback!
                    </Typography>
                  </Box>}
              </Box>}
          </Paper>
        </Container>
      </body>
    </html>;
}